import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { ColorScheme, PilotThemeConfig } from "./theme-config.js";
import { generateFullStylesheet } from "./css-generator.js";

const STORAGE_THEME_KEY = "pilot.theme";
const STORAGE_SCHEME_KEY = "pilot.scheme";
const STYLE_ELEMENT_ID = "pilot-theme-stylesheet";

interface ThemeContextValue {
  themeName: string;
  scheme: ColorScheme;
  setTheme: (name: string) => void;
  setScheme: (scheme: ColorScheme) => void;
  themes: PilotThemeConfig[];
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export interface ThemeProviderProps {
  themes: PilotThemeConfig[];
  defaultTheme?: string;
  defaultScheme?: ColorScheme;
  children: ReactNode;
}

export function ThemeProvider({
  themes,
  defaultTheme,
  defaultScheme = "dark",
  children,
}: ThemeProviderProps) {
  if (themes.length === 0) {
    throw new Error("ThemeProvider: themes prop must contain at least one theme");
  }

  const resolveThemeName = (candidate: string | undefined): string =>
    candidate && themes.some((t) => t.name === candidate) ? candidate : themes[0].name;

  const [themeName, setThemeNameState] = useState<string>(resolveThemeName(defaultTheme));
  const [scheme, setSchemeState] = useState<ColorScheme>(defaultScheme);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const storedTheme = window.localStorage.getItem(STORAGE_THEME_KEY);
    const storedScheme = window.localStorage.getItem(STORAGE_SCHEME_KEY);
    setThemeNameState(resolveThemeName(storedTheme ?? defaultTheme));
    if (storedScheme === "dark" || storedScheme === "light") {
      setSchemeState(storedScheme);
    }
    // resolveThemeName closes over `themes` and `defaultTheme`; both are
    // declared above and don't need to be in deps for the lint rule.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themes, defaultTheme]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    let style = document.getElementById(STYLE_ELEMENT_ID) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement("style");
      style.id = STYLE_ELEMENT_ID;
      document.head.appendChild(style);
    }
    style.textContent = generateFullStylesheet(themes);
  }, [themes]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-theme", themeName);
    document.documentElement.setAttribute("data-color-scheme", scheme);
  }, [themeName, scheme]);

  const setTheme = useCallback(
    (name: string) => {
      if (!themes.some((t) => t.name === name)) {
        if (import.meta.env?.DEV) {
          console.warn(`ThemeProvider: unknown theme "${name}"`);
        }
        return;
      }
      setThemeNameState(name);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(STORAGE_THEME_KEY, name);
      }
    },
    [themes],
  );

  const setScheme = useCallback((next: ColorScheme) => {
    setSchemeState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_SCHEME_KEY, next);
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ themeName, scheme, setTheme, setScheme, themes }),
    [themeName, scheme, setTheme, setScheme, themes],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used inside a <ThemeProvider>");
  }
  return ctx;
}
